import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import adminService from "../../services/adminService";
import Footer from "../../components/layout/Footer";
import AdminHeader from "../../components/layout/AdminHeader";
import datasetViewConfig from "../../features/data-view/config/datasetViewConfig";

const sortFields = (fields = []) => [...new Set(fields)].sort((a, b) => a.localeCompare(b));

const arraysEqual = (left = [], right = []) => {
  if (left.length !== right.length) {
    return false;
  }

  return left.every((value, index) => value === right[index]);
};

const formatTimestamp = (value) => {
  if (!value) {
    return "Not configured yet";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString();
};

const FALLBACK_THEME = {
  dot: "bg-slate-400",
  accentText: "text-slate-200",
  subtleText: "text-slate-300",
  tabActive: "border-slate-400/40 bg-slate-500/10 text-slate-100",
  accentBadge: "border border-slate-400/30 bg-slate-500/10 text-slate-100",
  detailBorder: "border-slate-500/30",
  detailGlow: "from-slate-500/20",
  checkbox:
    "border-slate-300/40 text-slate-200 hover:border-slate-200/60 hover:bg-slate-900/70 focus-visible:ring-slate-300/25",
  checkboxChecked:
    "border-slate-100/75 from-slate-300 to-slate-400 shadow-[0_0_0_1px_rgba(226,232,240,0.18),0_10px_22px_rgba(100,116,139,0.24)]",
};

const AdminFieldAccess = () => {
  const navigate = useNavigate();
  const [datasets, setDatasets] = useState([]);
  const [selectedDataset, setSelectedDataset] = useState("");
  const [datasetDetailsByKey, setDatasetDetailsByKey] = useState({});
  const [selectedFields, setSelectedFields] = useState([]);
  const [fieldSearch, setFieldSearch] = useState("");
  const [loadingDatasets, setLoadingDatasets] = useState(true);
  const [loadingDetailByKey, setLoadingDetailByKey] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const datasetDetail = selectedDataset ? datasetDetailsByKey[selectedDataset] : null;
  const loadingDetail = Boolean(selectedDataset && loadingDetailByKey[selectedDataset]);
  const activeTheme = useMemo(
    () => datasetViewConfig[selectedDataset]?.theme || FALLBACK_THEME,
    [selectedDataset]
  );
  const saveButtonClassName = `${activeTheme.accentBadge} rounded-xl px-5 py-3 text-sm font-semibold transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50`;

  const baselineFields = useMemo(
    () => sortFields(datasetDetail?.effective_allowed_fields || []),
    [datasetDetail]
  );
  const normalizedSelectedFields = useMemo(
    () => sortFields(selectedFields),
    [selectedFields]
  );
  const visibleFields = useMemo(() => {
    const searchTerm = fieldSearch.trim().toLowerCase();
    const allFields = datasetDetail?.available_fields || [];

    if (!searchTerm) {
      return allFields;
    }

    return allFields.filter((fieldName) => fieldName.toLowerCase().includes(searchTerm));
  }, [datasetDetail, fieldSearch]);
  const isDirty = useMemo(
    () => !arraysEqual(normalizedSelectedFields, baselineFields),
    [baselineFields, normalizedSelectedFields]
  );

  useEffect(() => {
    const user = localStorage.getItem("user");
    const token = localStorage.getItem("access_token");

    if (!token || !user) {
      navigate("/admin/login", { replace: true });
      return;
    }

    try {
      const parsedUser = JSON.parse(user);
      if (parsedUser.user_type !== "admin") {
        navigate("/admin/login", { replace: true });
        return;
      }
    } catch (err) {
      navigate("/admin/login", { replace: true });
      return;
    }

    loadDatasets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  useEffect(() => {
    if (!selectedDataset) {
      return;
    }

    setFieldSearch("");
    const cachedDetail = datasetDetailsByKey[selectedDataset];
    if (cachedDetail) {
      setSelectedFields(cachedDetail.effective_allowed_fields || []);
      return;
    }

    loadDatasetDetail(selectedDataset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDataset]);

  const loadDatasets = async () => {
    setLoadingDatasets(true);
    setError("");

    try {
      const response = await adminService.getFieldAccessDatasets();
      const nextDatasets = response?.data?.datasets || [];
      setDatasets(nextDatasets);

      if (nextDatasets.length > 0) {
        setSelectedDataset((current) => {
          if (current && nextDatasets.some((item) => item.dataset_key === current)) {
            return current;
          }
          return nextDatasets[0].dataset_key;
        });
      }
    } catch (err) {
      setError(err.message || "Failed to load datasets");
    } finally {
      setLoadingDatasets(false);
    }
  };

  const loadDatasetDetail = async (datasetKey, extraParams = {}, options = {}) => {
    const forceRefresh = Boolean(options.forceRefresh);
    const cachedDetail = datasetDetailsByKey[datasetKey];
    if (cachedDetail && !forceRefresh) {
      setSelectedFields(cachedDetail.effective_allowed_fields || []);
      return;
    }

    setLoadingDetailByKey((current) => ({
      ...current,
      [datasetKey]: true,
    }));
    setError("");
    setMessage("");

    try {
      const response = await adminService.getFieldAccessDetail(datasetKey, extraParams);
      const detail = response?.data || null;
      setDatasetDetailsByKey((current) => ({
        ...current,
        [datasetKey]: detail,
      }));
      if (selectedDataset === datasetKey) {
        setSelectedFields(detail?.effective_allowed_fields || []);
      }
    } catch (err) {
      setError(err.message || "Failed to load dataset fields");
      if (selectedDataset === datasetKey) {
        setSelectedFields([]);
      }
    } finally {
      setLoadingDetailByKey((current) => ({
        ...current,
        [datasetKey]: false,
      }));
    }
  };

  const handleToggleField = (fieldName) => {
    setSelectedFields((currentFields) => {
      if (currentFields.includes(fieldName)) {
        return currentFields.filter((value) => value !== fieldName);
      }

      return sortFields([...currentFields, fieldName]);
    });
  };

  const handleBulkUpdate = (mode) => {
    const visibleFieldSet = new Set(visibleFields);

    if (mode === "select") {
      setSelectedFields((currentFields) => sortFields([...currentFields, ...visibleFields]));
      return;
    }

    setSelectedFields((currentFields) =>
      currentFields.filter((fieldName) => !visibleFieldSet.has(fieldName))
    );
  };

  const handleSave = async () => {
    if (!selectedDataset) {
      return;
    }

    setSaving(true);
    setError("");
    setMessage("");

    try {
      const response = await adminService.updateFieldAccess(
        selectedDataset,
        normalizedSelectedFields
      );
      const detail = response?.data || null;
      setDatasetDetailsByKey((current) => ({
        ...current,
        [selectedDataset]: detail,
      }));
      setSelectedFields(detail?.effective_allowed_fields || []);
      setMessage(response?.message || "Field access updated");
      await loadDatasets();
    } catch (err) {
      setError(err.message || "Failed to save field access");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!selectedDataset) {
      return;
    }

    setSaving(true);
    setError("");
    setMessage("");

    try {
      const response = await adminService.resetFieldAccess(selectedDataset);
      const detail = response?.data || null;
      setDatasetDetailsByKey((current) => ({
        ...current,
        [selectedDataset]: detail,
      }));
      setSelectedFields(detail?.effective_allowed_fields || []);
      setMessage(response?.message || "Field access reset");
      await loadDatasets();
    } catch (err) {
      setError(err.message || "Failed to reset field access");
    } finally {
      setSaving(false);
    }
  };

  const handleRefresh = () => {
    if (!selectedDataset) {
      return;
    }

    loadDatasetDetail(selectedDataset, { refresh: 1 }, { forceRefresh: true });
  };

  const selectedFieldSet = new Set(selectedFields);

  return (
    <>
    <div className="min-h-screen w-full overflow-x-hidden bg-[#111318] text-slate-200">
      <AdminHeader
        title="Field Access"
        subtitle="Control which MongoDB fields regular users can see"
      />

      <div className="mx-auto max-w-[1440px] px-4 py-6 sm:px-6 lg:px-10 lg:py-10">
        <div className="space-y-6">
          {(message || error) && (
            <div
              className={`rounded-xl border px-4 py-3 text-sm ${
                error
                  ? "border-red-500/40 bg-red-500/10 text-red-200"
                  : "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
              }`}
            >
              {error || message}
            </div>
          )}

          <section className="rounded-xl border border-slate-700/50 bg-slate-800/50 p-4 shadow-xl backdrop-blur-sm sm:p-6">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-50">Datasets</h2>
                <p className="text-sm text-slate-400">
                  Open a dataset to configure user-visible fields.
                </p>
              </div>
              <button
                type="button"
                onClick={loadDatasets}
                className="rounded-lg border border-slate-600 px-3 py-2 text-xs font-semibold text-slate-200 hover:border-slate-400 sm:self-auto"
              >
                Refresh datasets
              </button>
            </div>

            {loadingDatasets ? (
              <div className="rounded-xl border border-slate-800 bg-slate-950/50 px-4 py-6 text-sm text-slate-400">
                Loading datasets...
              </div>
            ) : (
              <div className="overflow-x-auto pb-1" data-testid="dataset-tabs-scroll">
                <div className="flex min-w-max gap-3">
                  {datasets.map((dataset) => {
                    const tabTheme = datasetViewConfig[dataset.dataset_key]?.theme || FALLBACK_THEME;

                    return (
                      <button
                        key={dataset.dataset_key}
                        type="button"
                        data-testid={`dataset-tab-${dataset.dataset_key}`}
                        onClick={() => setSelectedDataset(dataset.dataset_key)}
                        className={`rounded-xl border px-4 py-3 text-left transition ${
                          selectedDataset === dataset.dataset_key
                            ? tabTheme.tabActive
                            : "border-slate-800 bg-slate-950/50 hover:border-slate-600"
                        }`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="text-sm font-semibold text-slate-100">{dataset.name}</div>
                          <span className={`h-2 w-2 rounded-full ${tabTheme.dot}`} />
                        </div>
                        <div className="mt-1 text-xs text-slate-500">
                          {dataset.is_configured
                            ? `${dataset.allowed_field_count || 0} fields allowed`
                            : "All fields visible"}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </section>

          <section
            className={`w-full min-w-0 rounded-xl border bg-slate-800/50 p-4 shadow-xl backdrop-blur-sm sm:p-6 ${activeTheme.detailBorder}`}
            data-testid="detail-themed-panel"
          >
              {!selectedDataset || loadingDetail ? (
                <div className="rounded-2xl border border-slate-800 bg-slate-950/50 px-6 py-12 text-center text-slate-400">
                  {loadingDetail ? "Loading field access details..." : "Select a dataset to begin."}
                </div>
              ) : datasetDetail ? (
                <div className="space-y-6">
                  <div className={`pointer-events-none -mb-4 h-8 rounded-t-lg bg-gradient-to-b ${activeTheme.detailGlow} via-transparent to-transparent`} />
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-3">
                        <h2 className="break-words text-2xl font-semibold text-white">
                          {datasetDetail.name}
                        </h2>
                        <span
                          className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold ${
                            datasetDetail.is_configured
                              ? activeTheme.accentBadge
                              : "bg-slate-700/70 text-slate-300"
                          }`}
                          data-testid="detail-status-badge"
                        >
                          {datasetDetail.is_configured ? "Custom restrictions active" : "Default: all discovered fields"}
                        </span>
                      </div>
                      <p className="mt-2 break-all font-mono text-sm text-slate-400">
                        {datasetDetail.collection}
                      </p>
                      <p className="mt-3 break-words text-sm text-slate-400">
                        Last updated: {formatTimestamp(datasetDetail.updated_at)}
                        {datasetDetail.updated_by ? ` by ${datasetDetail.updated_by}` : ""}
                      </p>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-3">
                      <div className={`rounded-xl border border-slate-800 bg-slate-950/45 px-4 py-3 ${activeTheme.accentText}`}>
                        <div className="text-xs uppercase tracking-wide text-slate-500">Available</div>
                        <div className="mt-2 text-2xl font-semibold text-white">
                          {datasetDetail.available_fields?.length || 0}
                        </div>
                      </div>
                      <div className={`rounded-xl border border-slate-800 bg-slate-950/45 px-4 py-3 ${activeTheme.accentText}`}>
                        <div className="text-xs uppercase tracking-wide text-slate-500">Allowed</div>
                        <div className="mt-2 text-2xl font-semibold text-white">
                          {normalizedSelectedFields.length}
                        </div>
                      </div>
                      <div className={`rounded-xl border border-slate-800 bg-slate-950/45 px-4 py-3 ${activeTheme.accentText}`}>
                        <div className="text-xs uppercase tracking-wide text-slate-500">Sampled Docs</div>
                        <div className="mt-2 text-2xl font-semibold text-white">
                          {datasetDetail.sample_size || 0}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <input
                      type="text"
                      value={fieldSearch}
                      onChange={(event) => setFieldSearch(event.target.value)}
                      placeholder="Search fields..."
                      className={`w-full rounded-xl border border-slate-700 bg-slate-950/60 px-4 py-3 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 lg:max-w-md ${activeTheme.checkbox}`}
                    />

                    <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap lg:justify-end">
                      <button
                        type="button"
                        onClick={() => handleBulkUpdate("select")}
                        className="w-full rounded-xl border border-slate-600 px-4 py-3 text-sm font-semibold text-slate-200 hover:border-slate-400 sm:w-auto"
                      >
                        Select visible
                      </button>
                      <button
                        type="button"
                        onClick={() => handleBulkUpdate("clear")}
                        className="w-full rounded-xl border border-slate-600 px-4 py-3 text-sm font-semibold text-slate-200 hover:border-slate-400 sm:w-auto"
                      >
                        Clear visible
                      </button>
                      <button
                        type="button"
                        onClick={handleRefresh}
                        className={`w-full rounded-xl border px-4 py-3 text-sm font-semibold sm:w-auto ${activeTheme.accentBadge}`}
                      >
                        Refresh fields
                      </button>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-800 bg-slate-950/40">
                    <div className="flex flex-col gap-3 border-b border-slate-800 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
                      <div>
                        <h3 className="text-lg font-semibold text-white">Field list</h3>
                        <p className="text-sm text-slate-400">
                          Toggle the fields users should receive from this dataset.
                        </p>
                      </div>
                      <div className="text-sm text-slate-400">
                        Showing {visibleFields.length} of {datasetDetail.available_fields?.length || 0}
                      </div>
                    </div>

                    {visibleFields.length === 0 ? (
                      <div className="px-5 py-10 text-center text-slate-400">
                        No fields match this search.
                      </div>
                    ) : (
                      <div
                        className="max-h-[55vh] overflow-y-auto p-4 sm:p-5"
                        data-testid="field-list-scroll"
                      >
                        <div className="grid gap-3 md:grid-cols-2">
                          {visibleFields.map((fieldName) => {
                            const isSelected = selectedFieldSet.has(fieldName);

                            return (
                              <label
                                key={fieldName}
                                data-testid={`field-card-${fieldName}`}
                                className={`flex cursor-pointer items-start gap-3 rounded-xl border px-4 py-3 transition ${
                                  isSelected
                                    ? `${activeTheme.detailBorder} bg-slate-950/50`
                                    : "border-slate-800 bg-slate-950/50 hover:border-slate-600"
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => handleToggleField(fieldName)}
                                  className={`mt-1 h-4 w-4 rounded border-slate-500 bg-slate-900 bg-gradient-to-br ${activeTheme.checkbox} ${activeTheme.checkboxChecked}`}
                                />
                                <div className="min-w-0">
                                  <div className="break-all font-mono text-sm text-slate-100">
                                    {fieldName}
                                  </div>
                                  <div className="mt-1 text-xs text-slate-500">
                                    {isSelected ? "Users can access this field" : "Hidden from user-facing dataset responses"}
                                  </div>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-3 border-t border-slate-800 pt-2 sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-sm text-slate-400">
                      {isDirty ? "You have unsaved field changes." : "Field access is up to date."}
                    </div>

                    <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                      <button
                        type="button"
                        onClick={handleReset}
                        disabled={saving}
                        className="rounded-xl border border-slate-600 px-5 py-3 text-sm font-semibold text-slate-200 hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Reset to default
                      </button>
                      <button
                        type="button"
                        onClick={handleSave}
                        disabled={saving || !isDirty}
                        className={saveButtonClassName}
                        data-testid="save-button-themed"
                      >
                        {saving ? "Saving..." : "Save changes"}
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}
          </section>
        </div>
      </div>
    </div>
     <Footer />
     </>
  );
};

export default AdminFieldAccess;
